<template>
  <div class="plan-details">
    <p v-if="planHeader.length" class="plan-details__detail-header">
      {{ planHeader }}
    </p>
    <span
      v-for="detail in planDetails"
      :key="`detail-${detail.info}`"
      class="plan-details__detail-container"
    >
      <div v-if="detail.show" class="plan-details__detail">
        <div v-if="detail.show" class="plan-details__detail-wrapper">
          <div class="plan-details__icon-wrapper">
            <CheckmarkIcon v-if="detail.checked" class="plan-details__icon" />
            <XIcon v-else class="plan-details__icon plan-details__icon--x" />
          </div>
          <span
            class="plan-details__detail-info"
            :class="[
              detail.checked ? 'plan-details__detail-info--checked' : '',
              detail.checked && detail.customClass ? detail.customClass : '',
            ]"
            >{{ detail.info }}</span
          >
        </div>
      </div>
    </span>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import CheckmarkIcon from '@/assets/icons/greencheck_v2.svg'
import XIcon from '@/assets/icons/x_v2.svg'

import CONSTANTS from '@/constants'

export default {
  name: 'PlanListItemDetails',
  components: {
    CheckmarkIcon: { ...CheckmarkIcon, name: 'CheckMarkIcon' },
    XIcon: { ...XIcon, name: 'XIcon' },
  },
  props: {
    plan: {
      type: Object,
      required: true,
    },
  },
  computed: {
    ...mapGetters({
      isBlackFridayActive: 'app/isBlackFridayActive',
    }),
    ...mapState({
      planCodes: state => state.products.planCodes,
    }),
    isAnnualPlan() {
      return this.plan.merchant_id === CONSTANTS.PLANS.ANNUAL
    },
    planHeader() {
      return ''
    },
    planDetails() {
      const {
        PLANS: { ANNUAL, MONTHLY, PREMIUM, PTA, BAH, INE_PREMIUM_BOOGO_2Y },
        BLACK_FRIDAY_PRODUCT_BUNDLES: {
          INE_BLACK_FRIDAY_BUNDLE_ONE,
          INE_BLACK_FRIDAY_BUNDLE_TWO,
          INE_BLACK_FRIDAY_BUNDLE_THREE,
        },
        LEAP_YEAR_PROMO_PRODUCT_BUNDLES: { INE_LEAP_YEAR_BUNDLE },
        TCS_PROMO_PRODUCT_BUNDLES: { INE_TCS_BUNDLE },
      } = CONSTANTS
      const planId = this.plan.merchant_id || this.plan.slug

      const fiftyOffCertYearCHECKEDGroup = [
        PREMIUM,
        INE_BLACK_FRIDAY_BUNDLE_ONE,
        INE_BLACK_FRIDAY_BUNDLE_TWO,
        INE_TCS_BUNDLE,
      ]
      if (this.isBlackFridayActive) {
        fiftyOffCertYearCHECKEDGroup.push(PREMIUM)
      }
      return [
        {
          show: true,
          checked: [
            MONTHLY,
            ANNUAL,
            PREMIUM,
            PTA,
            BAH,
            INE_PREMIUM_BOOGO_2Y,
            INE_BLACK_FRIDAY_BUNDLE_ONE,
            INE_BLACK_FRIDAY_BUNDLE_TWO,
            INE_BLACK_FRIDAY_BUNDLE_THREE,
            INE_LEAP_YEAR_BUNDLE,
            INE_TCS_BUNDLE,
          ].includes(planId),
          info: 'Learning Paths',
        },
        {
          show: [INE_PREMIUM_BOOGO_2Y].includes(planId),
          checked: [INE_PREMIUM_BOOGO_2Y].includes(planId),
          info: 'Course Labs',
        },
        {
          show: ![INE_PREMIUM_BOOGO_2Y].includes(planId),
          checked: [
            MONTHLY,
            ANNUAL,
            PREMIUM,
            PTA,
            BAH,
            INE_BLACK_FRIDAY_BUNDLE_ONE,
            INE_BLACK_FRIDAY_BUNDLE_TWO,
            INE_BLACK_FRIDAY_BUNDLE_THREE,
            INE_LEAP_YEAR_BUNDLE,
            INE_TCS_BUNDLE,
          ].includes(planId),
          info: 'In-course Hands-on Labs',
        },
        {
          show: true,
          checked: [
            MONTHLY,
            ANNUAL,
            PREMIUM,
            PTA,
            BAH,
            INE_PREMIUM_BOOGO_2Y,
            INE_BLACK_FRIDAY_BUNDLE_ONE,
            INE_BLACK_FRIDAY_BUNDLE_TWO,
            INE_BLACK_FRIDAY_BUNDLE_THREE,
            INE_LEAP_YEAR_BUNDLE,
            INE_TCS_BUNDLE,
          ].includes(planId),
          info: 'Quizzes',
        },
        {
          show: true,
          checked: [
            MONTHLY,
            ANNUAL,
            PREMIUM,
            PTA,
            BAH,
            INE_PREMIUM_BOOGO_2Y,
            INE_BLACK_FRIDAY_BUNDLE_ONE,
            INE_BLACK_FRIDAY_BUNDLE_TWO,
            INE_BLACK_FRIDAY_BUNDLE_THREE,
            INE_LEAP_YEAR_BUNDLE,
            INE_TCS_BUNDLE,
          ].includes(planId),
          info: 'Course Files',
        },
        {
          show: [INE_PREMIUM_BOOGO_2Y].includes(planId),
          checked: [INE_PREMIUM_BOOGO_2Y].includes(planId),
          info: 'Learner Usage',
        },
        {
          show: ![INE_PREMIUM_BOOGO_2Y].includes(planId),
          checked: [
            MONTHLY,
            ANNUAL,
            PREMIUM,
            PTA,
            BAH,
            INE_BLACK_FRIDAY_BUNDLE_ONE,
            INE_BLACK_FRIDAY_BUNDLE_TWO,
            INE_BLACK_FRIDAY_BUNDLE_THREE,
            INE_LEAP_YEAR_BUNDLE,
            INE_TCS_BUNDLE,
          ].includes(planId),
          info: 'Usage Analytics',
        },
        {
          show: [
            MONTHLY,
            ANNUAL,
            PREMIUM,
            INE_BLACK_FRIDAY_BUNDLE_ONE,
            INE_BLACK_FRIDAY_BUNDLE_TWO,
            INE_BLACK_FRIDAY_BUNDLE_THREE,
            INE_TCS_BUNDLE,
          ].includes(planId),
          checked: [
            MONTHLY,
            ANNUAL,
            PREMIUM,
            INE_BLACK_FRIDAY_BUNDLE_ONE,
            INE_BLACK_FRIDAY_BUNDLE_TWO,
            INE_BLACK_FRIDAY_BUNDLE_THREE,
            INE_TCS_BUNDLE,
          ].includes(planId),
          info: '200+ Hours of Training Content (Novice Level)',
        },
        {
          show: [
            MONTHLY,
            ANNUAL,
            PREMIUM,
            INE_BLACK_FRIDAY_BUNDLE_ONE,
            INE_BLACK_FRIDAY_BUNDLE_TWO,
            INE_BLACK_FRIDAY_BUNDLE_THREE,
            INE_TCS_BUNDLE,
          ].includes(planId),
          checked: [
            PREMIUM,
            INE_BLACK_FRIDAY_BUNDLE_ONE,
            INE_BLACK_FRIDAY_BUNDLE_TWO,
            INE_TCS_BUNDLE,
          ].includes(planId),
          customClass: 'plan-details__detail-info--green',
          info: '3500+ Hours of Training Content (All Expertise Levels)',
        },
        {
          show: [MONTHLY, ANNUAL, PREMIUM, INE_TCS_BUNDLE].includes(planId),
          checked: [PREMIUM, INE_TCS_BUNDLE].includes(planId),
          info: '1000+ Hours of Hands-on Labs (All Expertise Levels)',
        },
        {
          show: [
            MONTHLY,
            ANNUAL,
            PREMIUM,
            INE_BLACK_FRIDAY_BUNDLE_ONE,
            INE_BLACK_FRIDAY_BUNDLE_TWO,
            INE_BLACK_FRIDAY_BUNDLE_THREE,
            INE_TCS_BUNDLE,
          ].includes(planId),
          checked: fiftyOffCertYearCHECKEDGroup.includes(planId),
          info: '50% Off One Certification Voucher/Year',
        },
        {
          show: ![
            MONTHLY,
            ANNUAL,
            PREMIUM,
            INE_BLACK_FRIDAY_BUNDLE_ONE,
            INE_BLACK_FRIDAY_BUNDLE_TWO,
            INE_BLACK_FRIDAY_BUNDLE_THREE,
            INE_TCS_BUNDLE,
          ].includes(planId),
          checked: [PTA, BAH, INE_PREMIUM_BOOGO_2Y, INE_LEAP_YEAR_BUNDLE].includes(planId),
          info: 'Fundamentals Content',
        },
        {
          show: ![
            MONTHLY,
            ANNUAL,
            PREMIUM,
            INE_BLACK_FRIDAY_BUNDLE_ONE,
            INE_BLACK_FRIDAY_BUNDLE_TWO,
            INE_BLACK_FRIDAY_BUNDLE_THREE,
            INE_TCS_BUNDLE,
          ].includes(planId),
          checked: [PTA, BAH, INE_PREMIUM_BOOGO_2Y, INE_LEAP_YEAR_BUNDLE].includes(planId),
          info: [PTA, BAH, INE_PREMIUM_BOOGO_2Y, INE_LEAP_YEAR_BUNDLE].includes(planId)
            ? 'Expanded Course Library*'
            : 'Expanded Course Library',
        },
        {
          show: ![
            MONTHLY,
            ANNUAL,
            PREMIUM,
            INE_PREMIUM_BOOGO_2Y,
            INE_BLACK_FRIDAY_BUNDLE_ONE,
            INE_BLACK_FRIDAY_BUNDLE_TWO,
            INE_BLACK_FRIDAY_BUNDLE_THREE,
            INE_TCS_BUNDLE,
          ].includes(planId),
          checked: [PTA, BAH, INE_LEAP_YEAR_BUNDLE].includes(planId),
          info: '50% off One Certification/Year',
        },
        {
          show: [INE_PREMIUM_BOOGO_2Y].includes(planId),
          checked: true,
          info: '50% off one (1) INE Security Certification at purchase, and 50% off one (1) INE Security Certification at year two (2)',
        },
      ]
    },
  },
}
</script>

<style lang="scss">
.plan-details {
  &__detail-container {
    &:nth-child(-n + 4) {
      .plan-details__detail-info {
        @media only screen and (max-width: 410px) {
          max-width: 105px;
        }
        max-width: 150px;
        @include tablet {
          max-width: 100%;
        }
      }
    }
  }
  &__detail {
    margin: 10px 0;
    display: flex;
    align-items: flex-start;
    font-family: var(--font-regular);
    color: var(--gray-8);
    width: 100%;
    white-space: wrap;
    font-size: rem(14px);
    line-height: 20px;
    @include mobile {
      margin: 5px 0;
    }
    &-info {
      text-align: left;
      overflow-wrap: break-word;
      &--checked {
        color: var(--white);
      }
      &--green {
        color: var(--strong-light-green-b5);
      }
    }
    &-header {
      margin: 10px 0;
      display: flex;
      align-items: center;
      font-family: var(--font-regular);
      width: 100%;
      white-space: nowrap;
      font-size: 16px;
      color: var(--white);
    }
    &-wrapper {
      width: 100%;
      display: grid;
      grid-template-columns: 23px 80%;
    }
  }
  &__icon {
    &-wrapper {
      display: flex;
      align-items: flex-start;
      justify-content: flex-start;
      padding-top: 3px;
    }
    width: 12px;
    height: 10px;
    margin-right: 8px;
    &--x {
      width: 15px;
      height: 15px;
    }
  }
}
</style>
